/**
 * VenueOS Frame SDK Client
 *
 * This module provides the client-side SDK for embedded applications
 * running inside the VenueOS shell iframe.
 */
import { buildApiFetchResponseFromPayload } from './api-error';
import { VENUEOS_MESSAGE_TYPES, VENUEOS_PROTOCOL_VERSION, isVenueOSMessage, isContextMessage, isMapDataResponseMessage, isApiResponseMessage, isMediaUrlResponseMessage, isTilesConfigResponseMessage, createHandshakeMessage, createMapDataRequestMessage, createApiRequestMessage, createShellUIRequestMessage, createMediaUrlRequestMessage, createTilesConfigRequestMessage, createRouteChangedMessage, } from './messages';
/** URL parameter name for the security token */
const VENUEOS_TOKEN_PARAM = '_venueos_token';
/**
 * Cached security token extracted at module load time.
 *
 * This is captured immediately when the SDK module is imported, BEFORE any
 * application code (React effects, routers, etc.) can modify the URL.
 * This ensures the token is preserved even if the app rewrites window.location.
 */
const CACHED_SECURITY_TOKEN = (() => {
    try {
        if (typeof window === 'undefined')
            return null;
        const params = new URLSearchParams(window.location.search);
        return params.get(VENUEOS_TOKEN_PARAM);
    }
    catch {
        return null;
    }
})();
function interpolatePath(template, pathParams) {
    if (!pathParams)
        return template;
    return template.replace(/\{(\w+)\}/g, (_, key) => {
        const value = pathParams[key];
        if (value === undefined) {
            throw new Error(`[VenueOS] Missing path parameter: ${key}`);
        }
        return encodeURIComponent(value);
    });
}
function buildQueryString(query) {
    if (!query)
        return '';
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null)
            continue;
        if (typeof value === 'string') {
            params.set(key, value);
        }
        else if (typeof value === 'number' || typeof value === 'boolean') {
            params.set(key, String(value));
        }
        else {
            params.set(key, JSON.stringify(value));
        }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}
/**
 * Compute the current route from `window.location`, stripping the
 * `_venueos_token` query parameter. Returns an absolute path within
 * the iframe's origin (e.g. `/leasing/agent?unit=123`).
 *
 * Exported for testing — applet developers should not need to call this.
 */
export function computeRouteSuffix() {
    const { pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);
    params.delete(VENUEOS_TOKEN_PARAM);
    const cleanSearch = params.size > 0 ? '?' + params.toString() : '';
    return pathname + cleanSearch + hash;
}
function extractErrorMessage(data) {
    if (typeof data === 'object' && data !== null) {
        const message = Reflect.get(data, 'message');
        if (typeof message === 'string' && message.length > 0) {
            return message;
        }
        const errorCode = Reflect.get(data, 'error');
        if (typeof errorCode === 'string' && errorCode.length > 0) {
            return errorCode;
        }
    }
    return undefined;
}
/**
 * Internal client implementation
 */
class VenueOSClientImpl {
    constructor(context, targetOrigin, trackRoute = true) {
        this._messageHandler = null;
        this._requestIdCounter = 0;
        this._lastReportedRoute = '';
        this._popstateHandler = null;
        this._hashchangeHandler = null;
        this._originalPushState = null;
        this._originalReplaceState = null;
        this._context = context;
        this._targetOrigin = targetOrigin;
        this._eventHandlers = new Map();
        this._pendingMapDataRequests = new Map();
        this._pendingApiRequests = new Map();
        this._pendingMediaRequests = new Map();
        this._pendingTilesConfigRequests = new Map();
        this._setupMessageListener();
        // Create the API client
        const typedRequest = async (method, template, init) => {
            // TypedFetchInit is a union (params optional); narrow for path interpolation
            const loose = init;
            const resolvedPath = interpolatePath(template, loose?.params?.path) + buildQueryString(loose?.params?.query);
            return this._apiFetch(resolvedPath, {
                method,
                body: loose?.body,
                headers: loose?.headers,
                timeout: loose?.timeout,
            });
        };
        this._api = {
            fetch: async (path, options = {}) => {
                return this._apiFetch(path, options);
            },
            get: async (path, init) => typedRequest('GET', path, init),
            post: async (path, init) => typedRequest('POST', path, init),
            put: async (path, init) => typedRequest('PUT', path, init),
            patch: async (path, init) => typedRequest('PATCH', path, init),
            delete: async (path, init) => typedRequest('DELETE', path, init),
        };
        // Create the CMS client
        this._cms = {
            list: async (contentTypeSlug, options) => {
                return this._cmsList(contentTypeSlug, options);
            },
            get: async (contentTypeSlug, entryId, options) => {
                return this._cmsGet(contentTypeSlug, entryId, options);
            },
            getFirst: async (contentTypeSlug, options) => {
                const result = await this._cmsList(contentTypeSlug, { ...options, limit: 1 });
                return result.entries[0] || null;
            },
            listAll: (contentTypeSlug, options) => {
                return this._cmsListAll(contentTypeSlug, options);
            },
            // Media resolution methods
            getMediaUrl: async (assetId) => {
                return this._getMediaUrl(assetId);
            },
            getMediaUrls: async (assetIds) => {
                return this._getMediaUrls(assetIds);
            },
            resolveMedia: async (assetId) => {
                return this._resolveMedia(assetId);
            },
            // Locale methods
            getLocales: async () => {
                return this._cmsGetLocales();
            },
        };
        if (trackRoute) {
            this._setupRouteTracking();
        }
    }
    get context() {
        return this._context;
    }
    get version() {
        return VENUEOS_PROTOCOL_VERSION;
    }
    get api() {
        return this._api;
    }
    get cms() {
        return this._cms;
    }
    get isPublic() {
        return this._context.isPublic ?? false;
    }
    on(event, handler) {
        if (!this._eventHandlers.has(event)) {
            this._eventHandlers.set(event, new Set());
        }
        this._eventHandlers.get(event)?.add(handler);
    }
    off(event, handler) {
        const handlers = this._eventHandlers.get(event);
        if (handlers) {
            handlers.delete(handler);
        }
    }
    async getMapData(options = {}) {
        const { timeout = 10000, mapId } = options;
        // Check if a map is selected (either from context or passed as parameter)
        if (!this._context.map && !mapId) {
            return Promise.reject(new Error('[VenueOS] No map selected. Select a map before requesting map data, or pass a mapId.'));
        }
        const requestId = `mapdata-${++this._requestIdCounter}-${Date.now()}`;
        return new Promise((resolve, reject) => {
            // Set up timeout
            const timeoutId = setTimeout(() => {
                this._pendingMapDataRequests.delete(requestId);
                reject(new Error(`[VenueOS] Timeout waiting for map data (${timeout}ms)`));
            }, timeout);
            // Store the pending request
            this._pendingMapDataRequests.set(requestId, {
                resolve: (mapData) => {
                    clearTimeout(timeoutId);
                    this._pendingMapDataRequests.delete(requestId);
                    resolve(mapData);
                },
                reject: (error) => {
                    clearTimeout(timeoutId);
                    this._pendingMapDataRequests.delete(requestId);
                    reject(error);
                },
            });
            // Send map data request to parent - include mapId if explicitly provided
            const request = createMapDataRequestMessage(requestId, mapId);
            window.parent.postMessage(request, this._targetOrigin);
        });
    }
    async getTilesConfig(options = {}) {
        const { timeout = 10000 } = options;
        const requestId = `tilesconfig-${++this._requestIdCounter}-${Date.now()}`;
        return new Promise((resolve, reject) => {
            // Set up timeout
            const timeoutId = setTimeout(() => {
                this._pendingTilesConfigRequests.delete(requestId);
                reject(new Error(`[VenueOS] Timeout waiting for tiles config (${timeout}ms)`));
            }, timeout);
            // Store the pending request
            this._pendingTilesConfigRequests.set(requestId, {
                resolve: (tiles) => {
                    clearTimeout(timeoutId);
                    this._pendingTilesConfigRequests.delete(requestId);
                    resolve(tiles);
                },
                reject: (error) => {
                    clearTimeout(timeoutId);
                    this._pendingTilesConfigRequests.delete(requestId);
                    reject(error);
                },
            });
            // Send tiles config request to parent
            const request = createTilesConfigRequestMessage(requestId);
            window.parent.postMessage(request, this._targetOrigin);
        });
    }
    setShellUI(options) {
        const message = createShellUIRequestMessage(options);
        window.parent.postMessage(message, this._targetOrigin);
    }
    setRoute(route) {
        const message = createRouteChangedMessage(route);
        window.parent.postMessage(message, this._targetOrigin);
    }
    destroy() {
        if (this._popstateHandler) {
            window.removeEventListener('popstate', this._popstateHandler);
            this._popstateHandler = null;
        }
        if (this._hashchangeHandler) {
            window.removeEventListener('hashchange', this._hashchangeHandler);
            this._hashchangeHandler = null;
        }
        if (this._originalPushState) {
            history.pushState = this._originalPushState;
            this._originalPushState = null;
        }
        if (this._originalReplaceState) {
            history.replaceState = this._originalReplaceState;
            this._originalReplaceState = null;
        }
        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler);
            this._messageHandler = null;
        }
        this._eventHandlers.clear();
        // Reject any pending map data requests
        this._pendingMapDataRequests.forEach((pending) => {
            pending.reject(new Error('[VenueOS] Client destroyed'));
        });
        this._pendingMapDataRequests.clear();
        // Reject any pending API requests
        this._pendingApiRequests.forEach((pending) => {
            pending.reject(new Error('[VenueOS] Client destroyed'));
        });
        this._pendingApiRequests.clear();
        // Reject any pending media requests
        this._pendingMediaRequests.forEach((pending) => {
            pending.reject(new Error('[VenueOS] Client destroyed'));
        });
        this._pendingMediaRequests.clear();
        // Reject any pending tiles config requests
        this._pendingTilesConfigRequests.forEach((pending) => {
            pending.reject(new Error('[VenueOS] Client destroyed'));
        });
        this._pendingTilesConfigRequests.clear();
    }
    /**
     * Internal method to make API requests through the shell
     */
    async _apiFetch(path, options = {}) {
        const { method = 'GET', body, headers, timeout = 30000 } = options;
        // Validate path starts with /api/
        if (!path.startsWith('/api/')) {
            return Promise.reject(new Error('[VenueOS] API path must start with /api/'));
        }
        const requestId = `api-${++this._requestIdCounter}-${Date.now()}`;
        return new Promise((resolve, reject) => {
            // Set up timeout
            const timeoutId = setTimeout(() => {
                this._pendingApiRequests.delete(requestId);
                reject(new Error(`[VenueOS] API request timeout (${timeout}ms): ${method} ${path}`));
            }, timeout);
            // Store the pending request
            this._pendingApiRequests.set(requestId, {
                resolve: (response) => {
                    clearTimeout(timeoutId);
                    this._pendingApiRequests.delete(requestId);
                    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- generic narrowing for the postMessage bridge
                    resolve(response);
                },
                reject: (error) => {
                    clearTimeout(timeoutId);
                    this._pendingApiRequests.delete(requestId);
                    reject(error);
                },
            });
            // Send API request to parent
            const request = createApiRequestMessage(requestId, path, method, body, headers);
            window.parent.postMessage(request, this._targetOrigin);
        });
    }
    /**
     * Internal method to list CMS content entries
     */
    async _cmsList(contentTypeSlug, options = {}) {
        const appSlug = this._context.app?.slug;
        if (!appSlug) {
            throw new Error('[VenueOS CMS] App slug not available in context. Ensure the app is properly initialized.');
        }
        let venueId;
        if (options.venueId === null) {
            venueId = undefined;
        }
        else if (options.venueId !== undefined) {
            venueId = options.venueId;
        }
        else {
            venueId = this._context.map?.id;
        }
        const response = await this._api.get('/api/cms/delivery/apps/{appSlug}/{typeSlug}', {
            params: {
                path: { appSlug, typeSlug: contentTypeSlug },
                query: {
                    venueId,
                    locale: options.locale,
                    limit: options.limit,
                    offset: options.offset,
                    expand: options.expand?.length ? options.expand.join(',') : undefined,
                },
            },
        });
        if (!response.ok) {
            const msg = extractErrorMessage(response.data);
            throw new Error(`[VenueOS CMS] Failed to fetch content: ${msg ?? String(response.status)}`);
        }
        /* oxlint-disable typescript/no-unsafe-type-assertion -- generic CMS data narrowing; callers provide T */
        const entries = (response.data.entries || []).map((entry) => ({
            id: entry.id,
            status: entry.status,
            data: entry.data,
            locale: entry.locale || 'en',
            publishedAt: entry.published_at ?? null,
            createdAt: entry.created_at,
            updatedAt: entry.updated_at,
        }));
        /* oxlint-enable typescript/no-unsafe-type-assertion */
        return {
            entries,
            total: response.data.total ?? entries.length,
        };
    }
    /**
     * Internal method to get a single CMS content entry
     */
    async _cmsGet(contentTypeSlug, entryId, options = {}) {
        const appSlug = this._context.app?.slug;
        if (!appSlug) {
            throw new Error('[VenueOS CMS] App slug not available in context.');
        }
        const response = await this._api.get('/api/cms/delivery/apps/{appSlug}/{typeSlug}/{entryId}', {
            params: {
                path: { appSlug, typeSlug: contentTypeSlug, entryId },
                query: {
                    expand: options.expand?.length ? options.expand.join(',') : undefined,
                },
            },
        });
        if (!response.ok) {
            if (response.status === 404)
                return null;
            const msg = extractErrorMessage(response.data);
            throw new Error(`[VenueOS CMS] Failed to fetch entry: ${msg ?? String(response.status)}`);
        }
        const entry = response.data.entry;
        if (!entry)
            return null;
        /* oxlint-disable typescript/no-unsafe-type-assertion -- generic CMS data narrowing; callers provide T */
        return {
            id: entry.id,
            status: entry.status,
            data: entry.data,
            locale: entry.locale || 'en',
            publishedAt: entry.published_at ?? null,
            createdAt: entry.created_at,
            updatedAt: entry.updated_at,
        };
        /* oxlint-enable typescript/no-unsafe-type-assertion */
    }
    /**
     * Internal async generator that paginates through all entries for a content type.
     *
     * Uses an explicit `for...of` + `yield` (rather than `yield*`) so that
     * generator `.return()` can interrupt between individual entries, and checks
     * an optional `AbortSignal` before every page fetch.
     */
    async *_cmsListAll(contentTypeSlug, options) {
        const { signal, ...listOptions } = options ?? {};
        let offset = 0;
        let total = Infinity;
        while (offset < total) {
            if (signal?.aborted) {
                throw signal.reason instanceof Error
                    ? signal.reason
                    : new DOMException('The operation was aborted.', 'AbortError');
            }
            const page = await this._cmsList(contentTypeSlug, { ...listOptions, offset });
            total = page.total;
            offset += page.entries.length;
            if (page.entries.length === 0)
                break;
            for (const entry of page.entries) {
                yield entry;
            }
        }
    }
    // =========================================================================
    // Media Resolution Methods
    // =========================================================================
    /**
     * Internal method to get a single media URL
     */
    async _getMediaUrl(assetId) {
        const urls = await this._getMediaUrls([assetId]);
        return urls[assetId] || null;
    }
    /**
     * Internal method to get multiple media URLs in a batch
     */
    async _getMediaUrls(assetIds) {
        if (assetIds.length === 0)
            return {};
        const requestId = `media-${++this._requestIdCounter}-${Date.now()}`;
        const timeout = 30000;
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                this._pendingMediaRequests.delete(requestId);
                reject(new Error(`[VenueOS] Media URL request timeout (${timeout}ms)`));
            }, timeout);
            this._pendingMediaRequests.set(requestId, {
                resolve: (payload) => {
                    clearTimeout(timeoutId);
                    this._pendingMediaRequests.delete(requestId);
                    if (payload.success && payload.urls) {
                        resolve(payload.urls);
                    }
                    else {
                        reject(new Error(payload.error || 'Failed to resolve media URLs'));
                    }
                },
                reject: (error) => {
                    clearTimeout(timeoutId);
                    this._pendingMediaRequests.delete(requestId);
                    reject(error);
                },
            });
            const request = createMediaUrlRequestMessage(requestId, assetIds, false);
            window.parent.postMessage(request, this._targetOrigin);
        });
    }
    /**
     * Internal method to resolve a media asset to full metadata
     */
    async _resolveMedia(assetId) {
        const requestId = `media-${++this._requestIdCounter}-${Date.now()}`;
        const timeout = 30000;
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                this._pendingMediaRequests.delete(requestId);
                reject(new Error(`[VenueOS] Media resolve request timeout (${timeout}ms)`));
            }, timeout);
            this._pendingMediaRequests.set(requestId, {
                resolve: (payload) => {
                    clearTimeout(timeoutId);
                    this._pendingMediaRequests.delete(requestId);
                    if (payload.success && payload.media) {
                        resolve(payload.media[assetId] || null);
                    }
                    else {
                        reject(new Error(payload.error || 'Failed to resolve media'));
                    }
                },
                reject: (error) => {
                    clearTimeout(timeoutId);
                    this._pendingMediaRequests.delete(requestId);
                    reject(error);
                },
            });
            const request = createMediaUrlRequestMessage(requestId, [assetId], true);
            window.parent.postMessage(request, this._targetOrigin);
        });
    }
    /**
     * Internal method to get available locales for the app
     */
    async _cmsGetLocales() {
        const appSlug = this._context.app?.slug;
        if (!appSlug) {
            throw new Error('[VenueOS CMS] App slug not available in context. Ensure the app is properly initialized.');
        }
        const response = await this._api.get('/api/cms/delivery/apps/{appSlug}/locales', {
            params: { path: { appSlug } },
        });
        if (!response.ok) {
            const msg = extractErrorMessage(response.data);
            throw new Error(`[VenueOS CMS] Failed to fetch locales: ${msg ?? String(response.status)}`);
        }
        return {
            locales: response.data.locales,
            defaultLocale: response.data.defaultLocale,
        };
    }
    _setupRouteTracking() {
        if (typeof window === 'undefined' || !window.location?.pathname)
            return;
        this._lastReportedRoute = computeRouteSuffix();
        this._popstateHandler = () => {
            this._onRouteChange();
        };
        window.addEventListener('popstate', this._popstateHandler);
        this._hashchangeHandler = () => {
            this._onRouteChange();
        };
        window.addEventListener('hashchange', this._hashchangeHandler);
        this._originalPushState = history.pushState.bind(history);
        this._originalReplaceState = history.replaceState.bind(history);
        const origPush = this._originalPushState;
        const origReplace = this._originalReplaceState;
        const onRouteChange = () => {
            this._onRouteChange();
        };
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- monkey-patching history requires matching the native signature
        history.pushState = ((...args) => {
            origPush(...args);
            onRouteChange();
        });
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- monkey-patching history requires matching the native signature
        history.replaceState = ((...args) => {
            origReplace(...args);
            onRouteChange();
        });
    }
    _onRouteChange() {
        const route = computeRouteSuffix();
        if (route === this._lastReportedRoute)
            return;
        this._lastReportedRoute = route;
        this.setRoute(route);
    }
    _setupMessageListener() {
        this._messageHandler = (event) => {
            if (this._targetOrigin !== '*' && event.origin !== this._targetOrigin) {
                return;
            }
            const data = event.data;
            if (!isVenueOSMessage(data)) {
                return;
            }
            if (isContextMessage(data) && data.type === VENUEOS_MESSAGE_TYPES.CONTEXT_UPDATED) {
                this._handleContextUpdate(data);
            }
            if (isMapDataResponseMessage(data)) {
                this._handleMapDataResponse(data);
            }
            if (isApiResponseMessage(data)) {
                this._handleApiResponse(data);
            }
            if (isMediaUrlResponseMessage(data)) {
                this._handleMediaUrlResponse(data);
            }
            if (isTilesConfigResponseMessage(data)) {
                this._handleTilesConfigResponse(data);
            }
        };
        window.addEventListener('message', this._messageHandler);
    }
    _handleContextUpdate(data) {
        this._context = data.payload;
        this._emit('context:updated', data.payload);
    }
    _handleMapDataResponse(data) {
        const { requestId, success, error, mapData } = data.payload;
        const pending = this._pendingMapDataRequests.get(requestId);
        if (pending) {
            if (success && mapData) {
                pending.resolve({
                    mvfUrl: mapData.mvfUrl,
                    updatedAt: mapData.updatedAt,
                    provider: mapData.provider,
                    tiles: mapData.tiles,
                    localePacks: mapData.localePacks,
                });
            }
            else {
                pending.reject(new Error(error || 'Failed to get map data'));
            }
        }
    }
    _handleApiResponse(data) {
        const { requestId } = data.payload;
        const pending = this._pendingApiRequests.get(requestId);
        if (pending) {
            pending.resolve(buildApiFetchResponseFromPayload(data.payload));
        }
    }
    _handleMediaUrlResponse(data) {
        const pending = this._pendingMediaRequests.get(data.payload.requestId);
        if (pending) {
            pending.resolve(data.payload);
        }
    }
    _handleTilesConfigResponse(data) {
        const { requestId, success, error, tiles } = data.payload;
        const pending = this._pendingTilesConfigRequests.get(requestId);
        if (pending) {
            if (success) {
                pending.resolve(tiles || null);
            }
            else {
                pending.reject(new Error(error || 'Failed to get tiles config'));
            }
        }
    }
    _emit(event, data) {
        const handlers = this._eventHandlers.get(event);
        if (handlers) {
            handlers.forEach((handler) => {
                try {
                    handler(data);
                }
                catch (error) {
                    console.error(`[VenueOS] Error in event handler for "${event}":`, error);
                }
            });
        }
    }
}
/**
 * VenueOS SDK namespace with static methods
 */
export const VenueOS = {
    /**
     * Check if the current page is embedded inside VenueOS
     */
    isEmbedded() {
        try {
            return window.self !== window.top;
        }
        catch {
            // If we can't access window.top due to cross-origin restrictions,
            // we're likely in an iframe
            return true;
        }
    },
    /**
     * Get the security token that was present in the URL when the SDK loaded.
     *
     * The token is cached at module load time to prevent issues with apps
     * that modify the URL (e.g., React Router, state sync to URL params).
     * This ensures the token is always available even if the URL has changed.
     *
     * @returns The cached token if it was present at load time, null otherwise
     */
    getTokenFromUrl() {
        return CACHED_SECURITY_TOKEN;
    },
    /**
     * Initialize the VenueOS client and receive context from the shell
     *
     * This method requires a security token to establish trust with the VenueOS shell.
     * The token is automatically extracted from the URL parameter `_venueos_token`,
     * or can be provided explicitly via options.
     *
     * @param options - Initialization options
     * @returns Promise that resolves with the VenueOS client instance
     * @throws Error if no security token is available
     *
     * @example
     * ```typescript
     * const venueos = await VenueOS.init();
     * console.log(venueos.context.venue?.name);
     *
     * venueos.on("context:updated", (newContext) => {
     *   console.log("Venue changed:", newContext.venue?.name);
     * });
     * ```
     */
    async init(options = {}) {
        const { timeout = 5000, targetOrigin = '*', trackRoute = true } = options;
        return new Promise((resolve, reject) => {
            // Check if we're in an iframe
            if (!VenueOS.isEmbedded()) {
                reject(new Error('[VenueOS] Not running inside an iframe. VenueOS.init() should only be called from embedded applications.'));
                return;
            }
            // Get security token from options or URL
            const token = options.token || VenueOS.getTokenFromUrl();
            if (!token) {
                reject(new Error('[VenueOS] Security token not found. Apps must be loaded through VenueOS to receive context. ' +
                    'Ensure the URL contains the _venueos_token parameter.'));
                return;
            }
            let timeoutId = null;
            let messageHandler = null;
            const cleanup = () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                if (messageHandler) {
                    window.removeEventListener('message', messageHandler);
                    messageHandler = null;
                }
            };
            // Set up timeout
            timeoutId = setTimeout(() => {
                cleanup();
                reject(new Error(`[VenueOS] Timeout waiting for context from VenueOS shell (${timeout}ms). ` +
                    'This may indicate a security token validation failure.'));
            }, timeout);
            // Listen for context message
            messageHandler = (event) => {
                // Validate origin if specified
                if (targetOrigin !== '*' && event.origin !== targetOrigin) {
                    return;
                }
                const data = event.data;
                if (!isVenueOSMessage(data)) {
                    return;
                }
                if (isContextMessage(data) && data.type === VENUEOS_MESSAGE_TYPES.CONTEXT) {
                    cleanup();
                    const client = new VenueOSClientImpl(data.payload, targetOrigin, trackRoute);
                    resolve(client);
                }
            };
            window.addEventListener('message', messageHandler);
            // Send handshake to parent with security token
            const handshake = createHandshakeMessage(token);
            window.parent.postMessage(handshake, targetOrigin);
        });
    },
};
//# sourceMappingURL=client.js.map